import { useState, useEffect } from 'react';
import { Menu, X, LogIn } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSiteConfig } from '../utils/siteConfig';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isDarkTop = isHome && !isScrolled;
  const { config, openGetStarted } = useSiteConfig();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Biggrow', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-[0_4px_20px_rgba(0,0,0,0.05)] py-3'
          : 'bg-gradient-to-b from-black/40 to-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={import.meta.env.BASE_URL + 'logo.png'} 
              alt="BigGroww Logo" 
              className={`h-10 w-auto object-contain transition-all duration-300 ${isDarkTop ? 'invert' : ''}`} 
              onError={(e) => (e.currentTarget.style.display = 'none')} 
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`transition-colors font-bold text-sm tracking-wide ${
                  isDarkTop 
                    ? 'text-white hover:text-[var(--accent)]' 
                    : 'text-slate-800 hover:text-primary'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={openGetStarted}
              className="px-6 py-2.5 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium hover:shadow-lg hover:shadow-primary/25 transition-all hover:-translate-y-0.5"
            >
              Get Started
            </button>
          </div>

          <button
            className={`md:hidden ${isDarkTop ? 'text-white' : 'text-slate-900'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 p-4 flex flex-col gap-4 bg-white/95 backdrop-blur-md border border-slate-200/80 shadow-2xl absolute left-6 right-6 top-full rounded-2xl">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-slate-800 hover:text-primary transition-colors font-bold p-2 text-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex flex-col gap-3 mt-2 pt-4 border-t border-slate-100">
              <button 
                onClick={() => { openGetStarted(); setIsMobileMenuOpen(false); }}
                className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium text-center"
              >
                Get Started
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
